import { useEffect, useState } from 'react'

import { FeatureFlagAvailabilitiesDocument, LoadFormDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { FormEditor } from 'modules/forms/components/newFormEditor/formEditor'
import { FromFragmentToQuestionnaire } from 'modules/forms/components/newFormEditor/helpers'

import { AgentStateType, QuestionnaireType } from './types'

const defaultState: QuestionnaireType = {
  title: '',
  description: '',
  elements: [],
}

const FormWithoutAgent = ({
  initialState,
  setForm,
}: {
  initialState: QuestionnaireType
  setForm: (form: QuestionnaireType) => void
}) => {
  const [state, setState] = useState<QuestionnaireType>(initialState)

  const handleChange = (questionnaire: QuestionnaireType) => {
    setState(questionnaire)
  }

  useEffect(() => {
    setForm(state)
  }, [state, setForm])

  return <FormEditor state={state} setState={handleChange} />
}

const FormWithAgent = ({
  initialState,
  setForm,
}: {
  initialState: QuestionnaireType
  setForm: (form: QuestionnaireType) => void
}) => {
  const { state, setState } = useCocreationAgent<AgentStateType, AgentStateType>({
    name: 'co-creation-workflow',
    initialState: {
      questionnaire: initialState,
      updateForm: true,
    },
    initialMessage: i18n.t('forms.one.chat_initial_message'),
  })

  useAutoOpenChat()

  useEffect(() => {
    if (state?.questionnaire && !isEqual(state.questionnaire, initialState)) {
      setForm(state.questionnaire)
    }
  }, [state, initialState, setForm])

  const handleChange = (questionnaire: QuestionnaireType) => {
    if (state) {
      setState?.({ ...state, questionnaire })
      setForm(questionnaire)
    }
  }

  if (!state) return null

  return <FormEditor state={state.questionnaire} setState={handleChange} />
}

interface Props {
  id?: string
  resourceType: string
  setForm: (form: QuestionnaireType) => void
}

const NewFormEditor = ({ id, resourceType, setForm }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data: featureFlagAvailabilities, loading: loadingFeatureFlags } = useQuery(
    FeatureFlagAvailabilitiesDocument,
    {
      variables: {
        companyId,
        featureNames: ['dev_ai_agent'],
      },
    }
  )

  const { data: formData, loading: loadingForm } = useQuery(LoadFormDocument, {
    variables: { id: id ?? '', resourceType },
    skip: !id,
  })

  if (loadingFeatureFlags || loadingForm) {
    return <Loading expand />
  }

  const agentEnabled =
    featureFlagAvailabilities?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_ai_agent'
    )?.enabled ?? false

  const initialState = formData?.forms?.form
    ? FromFragmentToQuestionnaire(formData.forms.form)
    : defaultState

  if (agentEnabled) {
    return <FormWithAgent initialState={initialState} setForm={setForm} />
  }

  return <FormWithoutAgent initialState={initialState} setForm={setForm} />
}

export default NewFormEditor
